/**
 * Shared helpers for admin downloadable PDF / Excel reports.
 */

export const formatDateForExport = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return dateString;
  }
};

export const todayFileStamp = (): string =>
  new Date().toISOString().split('T')[0];

export async function downloadExcelBuffer(
  buffer: ArrayBuffer | Uint8Array | BlobPart,
  fileName: string
): Promise<void> {
  const blob = new Blob([buffer as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function styleExcelHeaderRow(worksheet: any): void {
  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
}

type PdfDoc = {
  setFontSize: (n: number) => void;
  setFont: (name: string | undefined, style?: string) => void;
  text: (text: string | string[], x: number, y: number) => void;
  line: (x1: number, y1: number, x2: number, y2: number) => void;
  addPage: () => void;
  splitTextToSize: (text: string, size: number) => string[];
  getNumberOfPages: () => number;
  setPage: (n: number) => void;
  getTextWidth: (text: string) => number;
  save: (name: string) => void;
};

export function writeLandscapePdfTable(options: {
  title: string;
  subtitle?: string;
  headers: string[];
  colWidths: number[];
  rows: string[][];
  fileName: string;
}): void {
  import('jspdf')
    .then((jsPDFModule) => {
      const { jsPDF } = jsPDFModule;
      const doc = new jsPDF('landscape') as unknown as PdfDoc;

      doc.setFontSize(16);
      doc.text(options.title, 14, 15);

      let metaY = 22;
      if (options.subtitle) {
        doc.setFontSize(10);
        doc.text(options.subtitle, 14, metaY);
        metaY += 6;
      }
      doc.setFontSize(8);
      doc.text(`Exported: ${new Date().toLocaleString()}`, 14, metaY);

      const startY = metaY + 7;
      let currentY = startY;
      const headers = options.headers;
      const colWidths = options.colWidths;

      const drawHeader = () => {
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        let xPos = 14;
        headers.forEach((header, index) => {
          doc.text(header, xPos, currentY);
          xPos += colWidths[index];
        });
        currentY += 5;
        doc.line(14, currentY, 280, currentY);
        currentY += 3;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(7);
      };

      drawHeader();

      options.rows.forEach((row) => {
        if (currentY > 190) {
          doc.addPage();
          currentY = 20;
          drawHeader();
        }

        let xPos = 14;
        row.forEach((cell, cellIndex) => {
          const cellText = String(cell ?? 'N/A');
          const lines = doc.splitTextToSize(cellText, colWidths[cellIndex] - 2);
          doc.text(lines, xPos, currentY);
          xPos += colWidths[cellIndex];
        });

        const rowHeight =
          Math.max(
            ...row.map((cell, idx) => {
              const cellText = String(cell ?? 'N/A');
              const lines = doc.splitTextToSize(cellText, colWidths[idx] - 2);
              return lines.length * 4;
            })
          ) + 2;
        currentY += rowHeight;
      });

      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        const label = `Page ${i} of ${pageCount}`;
        doc.text(label, 280 - doc.getTextWidth(label), 200);
      }

      doc.save(options.fileName);
    })
    .catch((error) => {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please ensure jspdf is installed.');
    });
}
